import { Center, Flex, Heading, VStack, useToast } from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { Logo } from '../../components/shared-components/Logo'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import { OrganizationAdminForm } from './components/OrganizationAdminForm'
import {
  OrganizationAdminClient,
  OrganizationAdminFormData,
  setOrganizationAdmin,
} from './organizationAdminSlice'
import { Button } from '@chakra-ui/react'
import useApi from '../../common/utils/api/useApi'
import { Permission } from '../../types/permission'
import { Role as AdminRole } from '../../types/Role'
import { routes } from '../../pages/routes'
import { appDidFinishLaunching } from '../../app/appSlice'
import { useSSOClientConfig } from '../sso'
import { isEmpty } from 'lodash'
import { isEmailLike } from '../../utils/validation-helpers/emailFormatValidate'
import { getRequestErrorMessage } from '../../utils/api/getRequestErrorMessage'

const CreateOrganizationAdminProfilePage = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const dispatch = useAppDispatch()
  const { authentication } = useAppSelector((state) => state)
  const { selectedRole, organizationId } = useAppSelector((state) => state.onboardingContext)
  const { config: ssoClient, hasFixedOrganization: isClientWithPresetOrg } = useSSOClientConfig()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<OrganizationAdminFormData>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {},
  })

  useEffect(() => {
    if (authentication.firstName && !isEmailLike(authentication.firstName)) {
      setValue('givenName', authentication.firstName)
    }
    if (authentication.familyName && !isEmailLike(authentication.familyName)) {
      setValue('familyName', authentication.familyName)
    }
    if (authentication.email) setValue('email', authentication.email)
  }, [authentication, setValue])

  useEffect(() => {
    if (!selectedRole) {
      navigate(routes.selectRoles.path)
    } else if (selectedRole === 'prescriber') {
      navigate(routes.createPrescriberProfile.path)
    }
  }, [selectedRole, navigate])

  const getPermissionsForRole = (role: string): Permission[] => {
    switch (role) {
      case AdminRole.PATIENT_ADMIN:
        return [Permission.PATIENT_MANAGEMENT, Permission.ORGANIZATION_MANAGEMENT]
      case AdminRole.RX_ADMIN:
        return [
          Permission.PATIENT_MANAGEMENT,
          Permission.PRESCRIPTION_MANAGEMENT,
          Permission.ORGANIZATION_MANAGEMENT,
        ]
      default:
        return [Permission.PATIENT_MANAGEMENT, Permission.ORGANIZATION_MANAGEMENT]
    }
  }

  const { trigger: createAdminProfile, loading: createLoading } = useApi(
    OrganizationAdminClient.createAdminProfile,
    {
      onSuccess: async ({ data }) => {
        if (data) {
          dispatch(setOrganizationAdmin(data))
          await dispatch(appDidFinishLaunching())

          if (isClientWithPresetOrg) {
            navigate(routes.patients.path)
          } else {
            navigate(routes.settings.path)
          }
        }
      },
      onFailure: (error) => {
        console.error('Failed to create admin profile:', error)
        toast({
          title:
            getRequestErrorMessage(error) ??
            'Failed to create your profile. Please try again later.',
          status: 'error',
          duration: 5000,
          position: 'top',
          isClosable: true,
        })
      },
    }
  )

  const onSubmit = async (data: OrganizationAdminFormData) => {
    const permissions = getPermissionsForRole(selectedRole as string)
    const orgId = isClientWithPresetOrg ? ssoClient?.orgId : organizationId

    if (!orgId) {
      console.error('No organization ID available')
      return
    }

    await createAdminProfile({
      givenName: data.givenName,
      familyName: data.familyName,
      email: data.email,
      organizationId: orgId,
      permissions,
      userId: authentication.userId || '',
    })
  }

  const getRoleDisplayName = (role: string): string => {
    switch (role) {
      case AdminRole.PATIENT_ADMIN:
        return 'Patient Admin'
      case AdminRole.RX_ADMIN:
        return 'RxAdmin'
      default:
        return 'Admin'
    }
  }

  return (
    <VStack
      as="form"
      overflow="auto"
      p="24px"
      pb="9"
      minHeight="100%"
      spacing="12"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Center>
        <Logo maxWidth={100} />
      </Center>

      <Flex
        shadow="2xl"
        borderRadius="md"
        p="12"
        gap="6"
        direction="column"
        align="center"
        minWidth="800px"
      >
        <Heading as="h2" textAlign="center" size="lg">
          Create {getRoleDisplayName(selectedRole as string)} Profile
        </Heading>

        <OrganizationAdminForm register={register} errors={errors} />

        <Button
          type="submit"
          colorScheme="blue"
          size="lg"
          width="200px"
          isLoading={createLoading}
          disabled={!isEmpty(errors) || createLoading}
        >
          Create Profile
        </Button>
      </Flex>
    </VStack>
  )
}

export default CreateOrganizationAdminProfilePage
